/**
 * Pure DOM helpers for sticky-note rich-text formatting.
 * Extracted so they can be unit-tested independently of the React component.
 */

export const EMPTY_LINE_HTML = '<div><br></div>';

/** True when n is a top-level line block of root. */
const isRootLineBlock = (n: Node, root: HTMLElement): boolean =>
  (n instanceof HTMLDivElement &&
    (n as HTMLDivElement).parentElement === root) ||
  n instanceof HTMLUListElement ||
  n instanceof HTMLOListElement;

/** FigJam-style line block: a direct child div of the editor, or a list item. */
export const getLineBlock = (
  node: Node | null,
  root: HTMLElement,
): HTMLElement | null => {
  if (!node) return null;
  let current: Node | null =
    node.nodeType === Node.TEXT_NODE ? node.parentNode : node;
  while (current && current !== root) {
    if (current instanceof HTMLLIElement) return current;
    if (
      current instanceof HTMLDivElement &&
      (current as HTMLDivElement).parentElement === root
    ) {
      return current as HTMLElement;
    }
    current = current.parentNode;
  }
  return null;
};

/**
 * Ensure every direct child of root is a recognised line block (div or ul/ol).
 * Orphan runs — text nodes, <br>s, loose inlines — are wrapped into new divs
 * interleaved with existing line blocks.
 *
 * Previous implementation bailed early when ANY line block existed, leaving
 * sibling orphan nodes invisible to collectLineBlocks (bug: only first line
 * received font-size on mixed-content selections).
 */
export const normalizeLineBlocks = (root: HTMLElement): void => {
  if (root.childNodes.length === 0) {
    root.innerHTML = EMPTY_LINE_HTML;
    return;
  }

  const children = Array.from(root.childNodes);

  // Fast path: everything is already a recognised line block.
  if (children.every((n) => isRootLineBlock(n, root))) return;

  // Rebuild root's child order, collecting consecutive orphan runs into divs.
  const newChildren: Node[] = [];
  let orphanRun: Node[] = [];

  const flushOrphans = () => {
    if (orphanRun.length === 0) return;
    const div = document.createElement('div');
    orphanRun.forEach((n) => div.appendChild(n));
    newChildren.push(div);
    orphanRun = [];
  };

  for (const child of children) {
    if (isRootLineBlock(child, root)) {
      flushOrphans();
      newChildren.push(child);
    } else {
      orphanRun.push(child);
    }
  }
  flushOrphans();

  root.replaceChildren(...newChildren);
};

/** Collect all line-level elements: direct-child divs and list items (li). */
export const collectLineBlocks = (root: HTMLElement): HTMLElement[] => {
  const lines: HTMLElement[] = [];
  root.childNodes.forEach((child) => {
    if (child instanceof HTMLDivElement) {
      lines.push(child);
    } else if (
      child instanceof HTMLUListElement ||
      child instanceof HTMLOListElement
    ) {
      child.querySelectorAll('li').forEach((li) => lines.push(li));
    }
  });
  return lines;
};

/**
 * Return all line blocks whose range spans from the line containing startNode
 * to the line containing endNode, inclusive.
 *
 * Used internally and in unit tests. Production code prefers
 * getLineBlocksInRange which uses Range.intersectsNode for accuracy.
 */
export const getLineBlocksBetween = (
  root: HTMLElement,
  startNode: Node,
  endNode: Node,
): HTMLElement[] => {
  normalizeLineBlocks(root);

  let startLine = getLineBlock(startNode, root);
  let endLine = getLineBlock(endNode, root);

  const allLines = collectLineBlocks(root);

  // Caret on root itself (empty note) — use first / last available line.
  if (!startLine) startLine = allLines[0] ?? null;
  // endLine unresolved (selection ends at a root boundary or on the root itself).
  if (!endLine) endLine = allLines[allLines.length - 1] ?? startLine;

  if (!startLine) return [];
  if (!endLine || startLine === endLine) return [startLine];

  const startIdx = allLines.indexOf(startLine);
  const endIdx = allLines.indexOf(endLine);
  if (startIdx === -1 || endIdx === -1) return [startLine];

  const [from, to] =
    startIdx <= endIdx ? [startIdx, endIdx] : [endIdx, startIdx];
  return allLines.slice(from, to + 1);
};

/**
 * Return all line blocks that the given Range overlaps.
 *
 * Uses Range.intersectsNode so the browser resolves containment directly,
 * avoiding the start/end-container walking edge cases in getLineBlocksBetween
 * (e.g. Ctrl+A in Chrome sets startContainer/endContainer to the root element
 * itself rather than the first/last text nodes).
 *
 * For a collapsed range (caret) this is equivalent to getLineBlock.
 */
export const getLineBlocksInRange = (
  root: HTMLElement,
  range: Range,
): HTMLElement[] => {
  // Captured before normalizeLineBlocks runs: when root has orphan children,
  // normalizeLineBlocks rebuilds them via replaceChildren, which removes every
  // original child node. Per the DOM Range spec that decrements a root-anchored
  // range's end offset on each removal, collapsing a full Ctrl+A selection
  // (root, 0)-(root, childNodes.length) down to a caret at (root, 0).
  const spansEntireRoot =
    !range.collapsed &&
    range.startContainer === root &&
    range.startOffset === 0 &&
    range.endContainer === root &&
    range.endOffset === root.childNodes.length;

  normalizeLineBlocks(root);
  const allLines = collectLineBlocks(root);
  if (allLines.length === 0) return [];

  if (spansEntireRoot) return allLines;

  if (range.collapsed) {
    const line =
      getLineBlock(range.startContainer, root) ?? allLines[0] ?? null;
    return line ? [line] : [];
  }

  const intersected = allLines.filter((line) => {
    try {
      return range.intersectsNode(line);
    } catch {
      return false;
    }
  });
  // Fallback: no intersection found — return the line closest to the selection.
  if (intersected.length === 0) {
    const fallback =
      getLineBlock(range.startContainer, root) ?? allLines[0] ?? null;
    return fallback ? [fallback] : [];
  }
  return intersected;
};

/**
 * Remove font-size and line-height from ALL descendant elements of line.
 * The previous implementation only unwrapped font-styled spans; after
 * insertUnorderedList the browser can nest styled divs inside li elements,
 * where the inner style wins over a newly set li font-size.
 */
export const stripInlineFontStyles = (line: HTMLElement): void => {
  line.querySelectorAll<HTMLElement>('[style]').forEach((el) => {
    el.style.removeProperty('font-size');
    el.style.removeProperty('line-height');
    if (!el.getAttribute('style')?.trim()) el.removeAttribute('style');
  });

  // Remove zero-width space text nodes left by prior font-size span injections.
  const toRemove: Node[] = [];
  const walker = document.createTreeWalker(line, NodeFilter.SHOW_TEXT);
  let textNode: Node | null;
  while ((textNode = walker.nextNode())) {
    if (textNode.textContent === '\u200B') toRemove.push(textNode);
  }
  toRemove.forEach((n) => n.parentNode?.removeChild(n));
};

/** Set font-size/line-height on a line, clearing any descendant overrides. */
export const applyFontSizeToLine = (
  line: HTMLElement,
  size: string,
  lineHeight: string,
): void => {
  stripInlineFontStyles(line);
  line.style.fontSize = size;
  line.style.lineHeight = lineHeight;
};

/**
 * When insertUnorderedList wraps an existing styled div inside a new li,
 * transfer the div's line-level size to the li and flatten the single-block
 * child so the li remains one logical line.
 */
export const flattenLiBlockChild = (li: HTMLLIElement): void => {
  const children = Array.from(li.childNodes);
  if (children.length !== 1) return;
  const only = children[0];
  if (!(only instanceof HTMLDivElement)) return;
  const div = only as HTMLDivElement;
  if (div.style.fontSize) {
    li.style.fontSize = div.style.fontSize;
    li.style.lineHeight = div.style.lineHeight || '';
  }
  while (div.firstChild) li.insertBefore(div.firstChild, div);
  li.removeChild(div);
};

/**
 * After insertUnorderedList, flatten nested styled divs inside li elements
 * and re-normalise the root so all line blocks are consistently collected.
 */
export const normalizeBulletList = (root: HTMLElement): void => {
  root.querySelectorAll<HTMLLIElement>('li').forEach(flattenLiBlockChild);
  normalizeLineBlocks(root);
};

export const getCaretOffsetInLine = (
  line: HTMLElement,
  range: Range,
): number => {
  const preRange = document.createRange();
  preRange.selectNodeContents(line);
  preRange.setEnd(range.startContainer, range.startOffset);
  return preRange.toString().replace(/\u200B/g, '').length;
};

export const setCaretInLine = (line: HTMLElement, offset: number): void => {
  const selection = window.getSelection();
  if (!selection) return;

  const walker = document.createTreeWalker(line, NodeFilter.SHOW_TEXT);
  let remaining = offset;
  let textNode: Node | null;
  while ((textNode = walker.nextNode())) {
    const len = textNode.textContent?.replace(/\u200B/g, '').length ?? 0;
    if (remaining <= len) {
      const range = document.createRange();
      range.setStart(textNode, remaining);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      return;
    }
    remaining -= len;
  }

  const range = document.createRange();
  range.selectNodeContents(line);
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);
};

/** Places the caret at the given viewport point if it falls inside root. */
export const setSelectionFromPoint = (
  root: HTMLElement,
  x: number,
  y: number,
): boolean => {
  const doc = document as Document & {
    caretPositionFromPoint?: (
      x: number,
      y: number,
    ) => { offsetNode: Node; offset: number } | null;
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
  };

  let range: Range | null = null;
  if (doc.caretPositionFromPoint) {
    const position = doc.caretPositionFromPoint(x, y);
    if (position) {
      range = document.createRange();
      range.setStart(position.offsetNode, position.offset);
      range.collapse(true);
    }
  } else if (doc.caretRangeFromPoint) {
    range = doc.caretRangeFromPoint(x, y);
  }

  if (!range || !root.contains(range.startContainer)) return false;

  const selection = window.getSelection();
  if (!selection) return false;
  selection.removeAllRanges();
  selection.addRange(range);
  return true;
};

export const selectLineBlocks = (
  lines: HTMLElement[],
  selection: Selection,
): void => {
  if (lines.length === 0) return;
  const range = document.createRange();
  range.setStart(lines[0], 0);
  const last = lines[lines.length - 1];
  range.setEnd(last, last.childNodes.length);
  selection.removeAllRanges();
  selection.addRange(range);
};
